import _ from 'lodash';
import { uuid } from 'utils';
import { CreativeTemplatesEntities } from '../constants';
import { ArticleGroupAssetsQuery_articleGroup as ArticleGroup, CreativeSourceTypeEnum, MediaTypeEnum } from 'api';
import { AdCreativeEnum } from '../constants/AdCreative.enum';
import {
  CarouselCreative,
  Combinations,
  CreativeImage,
  CreativeTemplateInputs,
  TextAsset,
  CreativeVideo
} from '../types';

const getBlankTextAsset = () => ({ id: uuid(), isBlank: true, text: ' ' });

const getImages = (inputs: CreativeTemplateInputs) => [...inputs[CreativeTemplatesEntities.IMAGES]];
const getVideos = (inputs: CreativeTemplateInputs) => [...inputs[CreativeTemplatesEntities.VIDEOS]];

const getPostTexts = (inputs: CreativeTemplateInputs) => {
  const postTexts = [...inputs[CreativeTemplatesEntities.PostTexts]];

  if (!postTexts.length) postTexts.push(getBlankTextAsset());

  return postTexts;
};

const getHeadlines = (articleGroup: ArticleGroup, inputs: CreativeTemplateInputs) => {
  const headlines = [...inputs[CreativeTemplatesEntities.Headlines]];
  if (headlines.length) return headlines;

  const articleGroupTitle = articleGroup.title;
  if (articleGroupTitle) {
    headlines.push({ id: uuid(), text: articleGroupTitle });
  } else {
    headlines.push(getBlankTextAsset());
  }

  return headlines;
};

const getDescriptions = (articleGroup: ArticleGroup, inputs: CreativeTemplateInputs) => {
  const descriptions = [...inputs[CreativeTemplatesEntities.Descriptions]];
  if (descriptions.length) return descriptions;

  const articleGroupDescription = articleGroup.description;
  if (articleGroupDescription) {
    descriptions.push({ id: uuid(), text: articleGroupDescription });
  } else {
    descriptions.push(getBlankTextAsset());
  }

  return descriptions;
};

const getCallToActions = (inputs: CreativeTemplateInputs) => {
  const callToActions: (TextAsset | null)[] = [...inputs[CreativeTemplatesEntities.CTAs]];

  if (!callToActions.length) callToActions.push(null);

  return callToActions;
};

export const generateCreativeTemplateCombinations = (
  articleGroup: ArticleGroup,
  imageOrVideoCreativeInputs: CreativeTemplateInputs,
  carouselCreatives: CarouselCreative[]
) => {
  const imageOrVideoCombinations: Combinations = [];

  const normalizedInputs: CreativeTemplateInputs = {
    images: getImages(imageOrVideoCreativeInputs) as CreativeImage[],
    videos: getVideos(imageOrVideoCreativeInputs) as CreativeVideo[],
    postTexts: getPostTexts(imageOrVideoCreativeInputs),
    headlines: getHeadlines(articleGroup, imageOrVideoCreativeInputs),
    descriptions: getDescriptions(articleGroup, imageOrVideoCreativeInputs),
    ctas: getCallToActions(imageOrVideoCreativeInputs) as TextAsset[]
  };

  normalizedInputs.images.forEach((image) => {
    normalizedInputs.postTexts.forEach((postText) => {
      normalizedInputs.headlines.forEach((headline) => {
        normalizedInputs.descriptions.forEach((description) => {
          normalizedInputs.ctas.forEach((cta) => {
            const id = uuid();
            const sourceType = CreativeSourceTypeEnum.none;
            const isSelected = true;
            const facebookPreviewIframe = null;
            const adCreativeType = AdCreativeEnum.ImageOrVideoCreative;
            imageOrVideoCombinations.push({
              id,
              mediaType: MediaTypeEnum.IMAGE,
              image,
              postText,
              headline,
              description,
              cta,
              sourceType,
              isSelected,
              facebookPreviewIframe,
              adCreativeType
            });
          });
        });
      });
    });
  });

  normalizedInputs.videos.forEach((video) => {
    normalizedInputs.postTexts.forEach((postText) => {
      normalizedInputs.headlines.forEach((headline) => {
        normalizedInputs.descriptions.forEach((description) => {
          normalizedInputs.ctas.forEach((cta) => {
            const id = uuid();
            const sourceType = CreativeSourceTypeEnum.none;
            const isSelected = true;
            const facebookPreviewIframe = null;
            const adCreativeType = AdCreativeEnum.ImageOrVideoCreative;
            imageOrVideoCombinations.push({
              id,
              mediaType: MediaTypeEnum.VIDEO,
              video,
              postText,
              headline,
              description,
              cta,
              sourceType,
              isSelected,
              facebookPreviewIframe,
              adCreativeType
            });
          });
        });
      });
    });
  });

  const carouselCombinations = _.map(carouselCreatives, (carousel) => ({
    id: carousel.id,
    isSelected: true,
    message: carousel.message,
    cta: carousel.callToAction,
    facebookPreviewIframe: null,
    sourceType: carousel.sourceType,
    carouselAssetSpecs: carousel.carouselAssetSpecs,
    adCreativeType: AdCreativeEnum.CarouselCreative
  }));

  return [...imageOrVideoCombinations, ...carouselCombinations] as Combinations;
};
